import { Color3, Color4 } from '@babylonjs/core/Maths/math.color'
import { Vector3 } from '@babylonjs/core/Maths/math.vector'
import { DefaultRenderingPipeline } from '@babylonjs/core/PostProcesses/RenderPipeline/Pipelines/defaultRenderingPipeline'
import { DirectionalLight } from '@babylonjs/core/Lights/directionalLight'
import { HemisphericLight } from '@babylonjs/core/Lights/hemisphericLight'
import { Scene } from '@babylonjs/core/scene'
import type { AbstractEngine } from '@babylonjs/core/Engines/abstractEngine'
import type { PlayerState } from '../contracts/game'
import { ChaseCameraController } from '../camera/ChaseCameraController'
import { DeathEffect } from '../effects/DeathEffect'
import type { QualityLevel } from './QualityManager'
import type { LocalProfile } from '../persistence/ProfileRepository'
import { PlayerView } from '../player/PlayerView'
import { MaterialLibrary } from '../rendering/MaterialLibrary'
import { WorldView } from '../rendering/WorldView'
import type { TrackChunkDefinition } from '../world/ChunkTypes'
import { AssetLoader } from '../assets/AssetLoader'
import { RivalView } from '../rivals/RivalView'
import type { RivalSnapshot } from '../rivals/RivalController'

export interface VisualDiagnostic {
  readonly name: string
  readonly material: string
  readonly vertices: number
  readonly radius: number
}

export class BabylonApp {
  public readonly scene: Scene
  private readonly engine: AbstractEngine
  private readonly materials: MaterialLibrary
  private readonly worldView: WorldView
  private readonly playerView: PlayerView
  private readonly camera: ChaseCameraController
  private readonly deathEffect: DeathEffect
  private readonly assetLoader: AssetLoader
  private readonly rivalView: RivalView
  private readonly pipeline: DefaultRenderingPipeline
  private readonly deathPosition = new Vector3()
  private profile: LocalProfile
  private quality: QualityLevel = 'high'
  private colliderDebug = false

  public constructor(engine: AbstractEngine, profile: LocalProfile) {
    this.engine = engine
    this.profile = profile
    this.scene = new Scene(engine)
    this.scene.clearColor = new Color4(0.004, 0.008, 0.018, 1)
    this.scene.ambientColor = new Color3(0.12, 0.17, 0.24)
    this.scene.fogMode = Scene.FOGMODE_EXP2
    this.scene.fogDensity = 0.0045
    this.scene.fogColor = new Color3(0.008, 0.018, 0.035)

    const ambient = new HemisphericLight('ambient', new Vector3(0.15, 1, -0.35), this.scene)
    ambient.intensity = 0.72
    ambient.diffuse = new Color3(0.42, 0.64, 0.84)
    ambient.groundColor = new Color3(0.025, 0.04, 0.07)
    const key = new DirectionalLight('key', new Vector3(-0.35, -0.8, 0.45), this.scene)
    key.intensity = 1.65
    key.diffuse = new Color3(0.55, 0.78, 1)
    key.position.set(12, 22, -18)

    this.materials = new MaterialLibrary(this.scene)
    this.materials.applyProfile(profile)
    this.camera = new ChaseCameraController(this.scene)
    this.worldView = new WorldView(this.scene, this.materials)
    this.playerView = new PlayerView(this.scene, this.materials)
    this.playerView.applyProfile(profile)
    this.deathEffect = new DeathEffect(this.scene, this.materials)
    this.rivalView = new RivalView(this.scene, this.materials)
    this.assetLoader = new AssetLoader(this.scene)
    this.colliderDebug = new URLSearchParams(window.location.search).get('debug') === 'colliders'
    this.worldView.setColliderDebug(this.colliderDebug)
    this.playerView.setColliderDebug(this.colliderDebug)
    this.pipeline = new DefaultRenderingPipeline('neon-pipeline', true, this.scene, [this.camera.camera])
    this.pipeline.fxaaEnabled = true
    this.pipeline.bloomEnabled = profile.bloom
    this.pipeline.bloomThreshold = 0.74
    this.pipeline.bloomWeight = 0.27
    this.pipeline.bloomKernel = 48
    this.pipeline.imageProcessingEnabled = true
    this.pipeline.imageProcessing.contrast = 1.12
    this.pipeline.imageProcessing.exposure = 1.1
    void this.preloadAssets()
  }

  public renderGame(
    player: Readonly<PlayerState>,
    chunks: readonly TrackChunkDefinition[],
    tick: number,
    interpolationAlpha: number,
    renderOriginZ: number,
    timeSeconds: number,
    deltaSeconds: number,
    speedNormalized: number,
    rivals: readonly RivalSnapshot[] = [],
  ): void {
    this.worldView.sync(chunks, renderOriginZ, tick)
    this.playerView.update(player, interpolationAlpha, renderOriginZ, speedNormalized, this.profile.reducedMotion)
    this.rivalView.update(rivals, renderOriginZ, speedNormalized, this.profile.reducedMotion)
    this.camera.update(player, renderOriginZ, deltaSeconds, speedNormalized, this.profile.reducedMotion)
    this.materials.update(timeSeconds, speedNormalized, this.profile.highContrast)
    this.deathEffect.update(deltaSeconds)
    this.scene.render()
  }

  public renderMenu(chunks: readonly TrackChunkDefinition[], timeSeconds: number, deltaSeconds: number): void {
    this.worldView.sync(chunks, 0, 0)
    this.camera.updateMenu(timeSeconds)
    this.playerView.updateMenu(timeSeconds)
    this.rivalView.hide()
    this.materials.update(timeSeconds, 0.16, this.profile.highContrast)
    this.deathEffect.update(deltaSeconds)
    this.scene.render()
  }

  public triggerDeath(player: Readonly<PlayerState>, renderOriginZ: number): void {
    this.deathPosition.set(player.position.x, player.position.y, player.position.z - renderOriginZ)
    this.deathEffect.trigger(this.deathPosition)
  }

  public snapCamera(player: Readonly<PlayerState>, renderOriginZ: number): void {
    this.camera.snap(player, renderOriginZ)
  }

  public applyProfile(profile: LocalProfile): void {
    this.profile = profile
    this.materials.applyProfile(profile)
    this.playerView.applyProfile(profile)
    this.pipeline.bloomEnabled = profile.bloom && this.quality !== 'low'
    this.scene.fogColor.copyFrom(this.materials.atmosphereColor.scale(0.18))
  }

  public applyQuality(level: QualityLevel): void {
    this.quality = level
    const scaling = level === 'high' ? 1 : level === 'medium' ? 1.28 : 1.72
    this.engine.setHardwareScalingLevel(scaling)
    this.pipeline.bloomEnabled = this.profile.bloom && level !== 'low'
    this.pipeline.bloomKernel = level === 'high' ? 48 : 24
    this.pipeline.fxaaEnabled = level !== 'low'
  }

  public resize(): void {
    this.engine.resize()
  }

  public toggleColliderDebug(): boolean {
    this.colliderDebug = !this.colliderDebug
    this.worldView.setColliderDebug(this.colliderDebug)
    this.playerView.setColliderDebug(this.colliderDebug)
    return this.colliderDebug
  }

  public visualDiagnostics(): readonly VisualDiagnostic[] {
    return this.scene.meshes
      .filter((mesh) => mesh.name.startsWith('hero'))
      .map((mesh) => ({
        name: mesh.name,
        material: mesh.material?.name ?? 'none',
        vertices: mesh.getTotalVertices(),
        radius: mesh.getBoundingInfo().boundingSphere.radiusWorld,
      }))
  }

  public dispose(): void {
    this.assetLoader.dispose()
    this.pipeline.dispose()
    this.materials.dispose()
    this.scene.dispose()
    this.engine.dispose()
  }

  private async preloadAssets(): Promise<void> {
    const packs = await this.assetLoader.preload()
    const hero = packs.find((pack) => pack.definition.id === 'hero-core')
    if (hero?.container !== null && hero?.container !== undefined) this.playerView.applyAssetContainer(hero.container)
  }
}
